import { blueprintMatchesRepo } from '@runloop/reflex-contract';
import type { Blueprint } from '@/lib/types';
import { BUILDING_STATUSES } from './useBlueprintFilters';

export interface FindRepoBlueprintOptions {
  /**
   * Also match a blueprint whose build is still in flight. Off by default: a
   * caller that wants to *use* the blueprint (the launch dialog, the persona
   * form) can only use a finished one. Callers asking "does this repo have a
   * blueprint at all" — the no-blueprint nudge — turn it on, so they stop
   * nudging as soon as a build exists instead of inviting a duplicate.
   */
  includeBuilding?: boolean;
}

/**
 * Find the blueprint whose `metadata.repo` matches the given repo slug. Either
 * side may be a bare `owner/name` or a full URL — both are normalized before
 * comparing (see {@link blueprintMatchesRepo}). Returns `undefined` when no
 * slug is provided or no blueprint matches.
 *
 * Used by the Launch dialog and the Persona edit form to auto-suggest the
 * blueprint that matches the currently selected git repo, and by the
 * no-blueprint nudge to decide whether to nudge at all. It shares its matcher
 * with `useAvailableBlueprints` so the blueprint the picker ranks first is the
 * one these callers select.
 */
export function findRepoBlueprint(
  blueprints: Blueprint[],
  repoSlug: string,
  { includeBuilding = false }: FindRepoBlueprintOptions = {},
): Blueprint | undefined {
  if (!repoSlug) return undefined;
  return blueprints.find((bp) => {
    const matchesStatus =
      bp.status === 'build_complete' || (includeBuilding && BUILDING_STATUSES.includes(bp.status));
    if (!matchesStatus) return false;
    return blueprintMatchesRepo(bp, repoSlug);
  });
}
